import type { Asset, StillImageThumbnailTier } from '../types'
import { isImageAsset } from './assetUtils'

/**
 * Base ladder — always present on any asset that has thumbnail metadata
 * (the backend generates all four, clamped to the source size, no upscaling).
 */
export const STILL_IMAGE_BASE_TIERS: readonly StillImageThumbnailTier[] = [240, 480, 720, 1080] as const
/**
 * High ladder — generated ONLY when the source's larger dimension exceeds the
 * tier (see {@link canSourceProvideHighTier}). Lets the canvas show a sharp
 * preview when zoomed into a large image without decoding the full original.
 */
export const STILL_IMAGE_HIGH_TIERS: readonly StillImageThumbnailTier[] = [2160, 4096] as const
/** Full selection ladder, ascending. */
export const STILL_IMAGE_PREVIEW_TIERS: readonly StillImageThumbnailTier[] = [
  ...STILL_IMAGE_BASE_TIERS,
  ...STILL_IMAGE_HIGH_TIERS,
] as const

function getDevicePixelRatio(): number {
  if (typeof window === 'undefined') return 1
  return window.devicePixelRatio || 1
}

function getImageAssetUri(asset: Asset): string | undefined {
  return asset.enhancedUri || asset.uri || asset.thumbnailUri || undefined
}

function maxAssetDimension(asset: Asset): number {
  if (typeof asset.width === 'number' && typeof asset.height === 'number') {
    return Math.max(asset.width, asset.height)
  }
  return 0
}

export function isHighStillImageTier(tier: StillImageThumbnailTier | 'original'): tier is 2160 | 4096 {
  return tier === 2160 || tier === 4096
}

/**
 * Whether a high tier can ever exist for this source. MUST match the backend's
 * `getApplicableStillImageTiers` (`max(srcW, srcH) > tier`). The app-triggered
 * high-tier backfill keys off this predicate; if it diverges from the backend's
 * generation rule, the canvas would request a tier the backend never produces
 * and loop forever (bounded only by the backfill retry backoff).
 */
export function canSourceProvideHighTier(asset: Asset, tier: StillImageThumbnailTier | 'original'): boolean {
  if (!isHighStillImageTier(tier)) return false
  return maxAssetDimension(asset) > tier
}

/** Largest available variant whose tier is ≤ `maxTierInclusive`. */
function pickBestAvailableVariantUri(asset: Asset, maxTierInclusive: number): string | undefined {
  let best: string | undefined
  for (const tier of STILL_IMAGE_PREVIEW_TIERS) {
    if (tier > maxTierInclusive) break
    const uri = asset.thumbnailVariants?.[tier]
    if (uri) best = uri
  }
  return best
}

export function hasThumbnailVariants(asset: Asset | undefined | null): boolean {
  if (!asset || !asset.thumbnailRevisionId || !asset.thumbnailVariants) {
    return false
  }
  // Only the BASE ladder is required. High tiers are optional add-ons; gating
  // on them would make every legacy asset (which never had them) read as
  // "no variants" and break preview selection.
  return STILL_IMAGE_BASE_TIERS.every(tier => Boolean(asset.thumbnailVariants?.[tier]))
}

export function hasStillImageThumbnailMetadata(asset: Asset | undefined | null): boolean {
  if (!asset || !isImageAsset(asset)) return false
  return hasThumbnailVariants(asset)
}

export interface StillImagePreviewSelectionOptions {
  renderedWidth?: number
  renderedHeight?: number
  devicePixelRatio?: number
  fallbackTier?: StillImageThumbnailTier
  allowOriginal?: boolean
  maxOriginalDimension?: number
}

export function getStillImagePreviewTier(
  options: StillImagePreviewSelectionOptions = {},
): StillImageThumbnailTier | 'original' {
  const {
    renderedWidth = 0,
    renderedHeight = 0,
    devicePixelRatio = getDevicePixelRatio(),
    fallbackTier = 720,
  } = options

  if (renderedWidth <= 0 && renderedHeight <= 0) {
    return fallbackTier
  }

  const requiredPixels = Math.ceil(Math.max(renderedWidth, renderedHeight, 1) * Math.max(devicePixelRatio, 1))
  for (const tier of STILL_IMAGE_PREVIEW_TIERS) {
    if (requiredPixels <= tier) return tier
  }
  return 'original'
}

function isOriginalWithinLimit(asset: Asset, maxOriginalDimension: number | undefined): boolean {
  if (maxOriginalDimension === undefined) return true
  return (
    typeof asset.width === 'number' &&
    typeof asset.height === 'number' &&
    asset.width <= maxOriginalDimension &&
    asset.height <= maxOriginalDimension
  )
}

export function getStillImagePreviewUri(
  asset: Asset | undefined | null,
  options: StillImagePreviewSelectionOptions = {},
): string | undefined {
  if (!asset) return undefined

  // Non-image assets (video/audio) with thumbnail variants: tier-select over
  // whatever variants exist, falling back to the best available tier.
  if (!isImageAsset(asset)) {
    if (hasThumbnailVariants(asset)) {
      const selectedTier = getStillImagePreviewTier(options)
      if (selectedTier !== 'original' && asset.thumbnailVariants?.[selectedTier]) {
        return asset.thumbnailVariants[selectedTier]
      }
      const cap = selectedTier === 'original' ? Number.POSITIVE_INFINITY : selectedTier
      return pickBestAvailableVariantUri(asset, cap) || asset.thumbnailUri || undefined
    }
    return asset.thumbnailUri || undefined
  }

  const originalUri = getImageAssetUri(asset)
  if (!hasStillImageThumbnailMetadata(asset)) {
    // No generated ladder — the original is the only image we have, at every
    // zoom (the zoom-based `allowOriginal` preference doesn't apply: there's no
    // smaller tier to prefer instead). The one hard rule is the texture limit:
    // a size-capped caller (the WebGPU canvas) must not receive an original
    // that is KNOWN to exceed the limit, or it black-screens the whole canvas.
    // Unknown dims and within-limit originals fall through so ordinary images
    // still render. (A rare dimensionless-and-oversized asset is left for a
    // future atlas-level decode-size guard.)
    if (
      options.maxOriginalDimension !== undefined &&
      typeof asset.width === 'number' &&
      typeof asset.height === 'number' &&
      Math.max(asset.width, asset.height) > options.maxOriginalDimension
    ) {
      return asset.thumbnailUri && asset.thumbnailUri !== originalUri ? asset.thumbnailUri : undefined
    }
    return asset.thumbnailUri || originalUri
  }

  const selectedTier = getStillImagePreviewTier(options)

  // Exact tier is materialized — use it.
  if (selectedTier !== 'original' && asset.thumbnailVariants?.[selectedTier]) {
    return asset.thumbnailVariants[selectedTier]
  }

  // The ideal is a high tier this source CAN provide, but it isn't generated yet.
  if (selectedTier !== 'original' && canSourceProvideHighTier(asset, selectedTier)) {
    // No texture cap means a non-canvas caller (DOM <img>, no 8192 limit). Those
    // callers don't run the high-tier backfill, so the tier may never arrive —
    // fall through to the full-res original, the historical behaviour. Only the
    // cap-enforced canvas path holds on the best lower tier while its backfill
    // runs (avoiding an oversized GPU texture).
    if (options.maxOriginalDimension === undefined && options.allowOriginal !== false && originalUri) {
      return originalUri
    }
    // Canvas path: app-triggered backfill is/should be in flight — show the best
    // lower generated tier as an interim, never the (potentially oversized) original.
    return pickBestAvailableVariantUri(asset, selectedTier) || asset.thumbnailUri || originalUri
  }

  // Otherwise the ideal is the original itself, or a high tier this source can
  // never provide (source ≤ tier). The original is the sharpest usable source —
  // use it when permitted and within the texture-size limit; else fall back to
  // the best available generated tier (never an oversized original).
  if (options.allowOriginal !== false && isOriginalWithinLimit(asset, options.maxOriginalDimension) && originalUri) {
    return originalUri
  }
  return pickBestAvailableVariantUri(asset, Number.POSITIVE_INFINITY) || asset.thumbnailUri || originalUri
}
